import { computed, reactive } from 'vue';

// Per-entity active-tab memory for an editor surface. The active editor tab
// (e.g. Editor / Preview) is remembered per entry for the session, keyed by a
// stable identifier (a battle-tactic id, a document title). Module-scoped state
// is intentionally avoided here — each surface owns its own instance.
//
// `currentKey` is a reactive getter for the selected entry's key; `fallback` is
// the tab used when an entry hasn't been visited yet. Call `setTab` to seed a
// freshly-created entry and `migrate` when an entry's key changes (e.g. a rename
// for title-keyed surfaces) so its remembered tab follows it.
export function useEntityTabs<T extends string>(currentKey: () => string | undefined, fallback: T) {
  const tabs = reactive<Record<string, T>>({});

  const currentTab = computed<T>({
    get: () => tabs[currentKey() ?? ''] ?? fallback,
    set: (tab) => {
      const key = currentKey();
      if (key) tabs[key] = tab;
    },
  });

  function setTab(key: string, tab: T): void {
    tabs[key] = tab;
  }

  function migrate(from: string, to: string): void {
    if (from === to) return;
    tabs[to] = tabs[from] ?? fallback;
    delete tabs[from];
  }

  return { currentTab, setTab, migrate };
}
